<template>
  <div>
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运营管理</el-breadcrumb-item>
      <el-breadcrumb-item>营运督导</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="box-card" style="margin-top: 15px;">
      <!-- 顶部筛选栏 -->
      <div class="search-bar">
        <div class="stat-card">
          <div class="stat-card-label">门店总数</div>
          <div class="stat-card-value">{{ projects.length }}</div>
        </div>
        <el-form :inline="true" size="small" class="search-form">
          <el-form-item label="月份">
            <el-date-picker
              v-model="selectedMonth"
              type="month"
              placeholder="选择月份"
              value-format="yyyy-MM"
              @change="loadBoard"
              style="width: 160px;">
            </el-date-picker>
          </el-form-item>

          <el-form-item v-if="showSupervisorFilter" label="营运督导">
            <el-select v-model="filterSupervisorId" placeholder="全部督导" clearable @change="loadBoard" style="width: 150px;">
              <el-option v-for="item in supervisorList" :key="item._id" :label="item.name" :value="item._id"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="门店">
            <el-input v-model="keyword" placeholder="搜索门店名称" clearable @clear="loadBoard" @keyup.enter.native="loadBoard" style="width: 180px;"></el-input>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="loadBoard">查询</el-button>
          </el-form-item>

          <el-form-item style="float: right;">
            <el-button-group>
              <el-button :type="viewMode === 'table' ? 'primary' : ''" @click="viewMode = 'table'">表格视图</el-button>
              <el-button :type="viewMode === 'card' ? 'primary' : ''" @click="viewMode = 'card'">卡片视图</el-button>
            </el-button-group>
          </el-form-item>
        </el-form>
      </div>

      <!-- 表格视图 -->
      <div v-if="viewMode === 'table'">
        <el-table :data="pagedProjects" border stripe v-loading="loading" style="width: 100%;">

          <el-table-column label="营运督导" width="100" align="center">
            <template slot-scope="scope">
              <span class="supervisor-link" @click="filterBySupervisor(scope.row)">{{ scope.row.supervisorName }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="projectName" label="门店名称" min-width="160">
            <template slot-scope="scope">
              <span>{{ scope.row.projectName }}</span>
            </template>
          </el-table-column>

          <el-table-column label="开业时间" width="110" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.openingAt | date }}</span>
            </template>
          </el-table-column>

          <el-table-column label="开业天数" width="90" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.openingDays !== null">{{ scope.row.openingDays }}天</span>
              <span v-else>--</span>
            </template>
          </el-table-column>

          <el-table-column v-for="i in 2" :key="'w' + i" :label="i + '周清'" width="160" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.weekly[i-1]" class="status-done" @click="viewRecord(scope.row, 'weekly', i)">已完成</span>
              <span v-else class="status-undone" @click="openSubmitDialog(scope.row, 'weekly', i)">未完成</span>
            </template>
          </el-table-column>

          <el-table-column label="巡检1" width="160" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.monthly[0]" class="status-done" @click="viewRecord(scope.row, 'monthly', 1)">已完成</span>
              <span v-else class="status-undone" @click="openSubmitDialog(scope.row, 'monthly', 1)">未完成</span>
            </template>
          </el-table-column>

          <el-table-column label="巡检2" width="160" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.monthly[1]" class="status-done" @click="viewRecord(scope.row, 'monthly', 2)">已完成</span>
              <span v-else class="status-undone" @click="openSubmitDialog(scope.row, 'monthly', 2)">未完成</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <el-pagination
        style="margin-top: 15px; text-align: right;"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="projects.length">
      </el-pagination>

      <!-- 卡片视图 -->
      <div v-if="viewMode === 'card'" class="card-grid">
        <el-card v-for="project in pagedProjects" :key="project.projectId" class="project-card" shadow="hover">
          <div slot="header" class="card-header">
            <span>{{ project.projectName }}</span>
            <el-tag size="small" type="info" class="supervisor-tag" @click.native="filterBySupervisor(project)">{{ project.supervisorName }}</el-tag>
          </div>
          <div class="card-opening-info">
            <span v-if="project.openingAt">开业：{{ project.openingAt | date }}（{{ project.openingDays }}天）</span>
            <span v-else class="no-opening">暂无开业记录</span>
          </div>
          <div class="card-progress">
            <span>{{ getCompletedCount(project) }} / 4</span>
            <el-progress :percentage="Math.round(getCompletedCount(project) / 4 * 100)" :stroke-width="10" style="flex: 1; margin-left: 10px;"></el-progress>
          </div>
          <div class="card-items">
            <div v-for="i in 2" :key="'cw' + i" class="card-item" @click="project.weekly[i-1] ? viewRecord(project, 'weekly', i) : openSubmitDialog(project, 'weekly', i)">
              <i :class="project.weekly[i-1] ? 'el-icon-success' : 'el-icon-circle-check'" :style="{ color: project.weekly[i-1] ? '#67C23A' : '#F56C6C' }"></i>
              <span>{{ i }}周清</span>
            </div>
            <div class="card-item" @click="project.monthly[0] ? viewRecord(project, 'monthly', 1) : openSubmitDialog(project, 'monthly', 1)">
              <i :class="project.monthly[0] ? 'el-icon-success' : 'el-icon-circle-check'" :style="{ color: project.monthly[0] ? '#67C23A' : '#F56C6C' }"></i>
              <span>巡检1</span>
            </div>
            <div class="card-item" @click="project.monthly[1] ? viewRecord(project, 'monthly', 2) : openSubmitDialog(project, 'monthly', 2)">
              <i :class="project.monthly[1] ? 'el-icon-success' : 'el-icon-circle-check'" :style="{ color: project.monthly[1] ? '#67C23A' : '#F56C6C' }"></i>
              <span>巡检2</span>
            </div>
          </div>
        </el-card>
      </div>
    </el-card>

    <!-- 提交弹窗 -->
    <el-dialog :title="submitDialogTitle" :visible.sync="submitDialogVisible" width="600px" @close="resetSubmitForm">
      <el-form :model="submitForm" label-width="80px">
        <el-form-item label="图片">
          <ImageUpload
            v-model="submitForm.images"
            :active="submitDialogVisible"
            :show-tip="true"
            @preview="handleUploadPreview"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="submitForm.remark" type="textarea" :rows="3" placeholder="请输入备注"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="submitDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitRecord">提交</el-button>
      </span>
    </el-dialog>

    <!-- 查看弹窗 -->
    <el-dialog :title="viewDialogTitle" :visible.sync="viewDialogVisible" width="600px">
      <div v-if="viewRecordData">
        <div v-if="viewRecordData.images && viewRecordData.images.length > 0" class="view-images">
          <img v-for="(img, index) in viewRecordData.images" :key="index" :src="base + img" class="view-image" @click="previewImage(base + img)">
        </div>
        <div v-else style="color: #909399;">暂无图片</div>
        <div v-if="viewRecordData.remark" style="margin-top: 15px;">
          <strong>备注：</strong>{{ viewRecordData.remark }}
        </div>
        <div style="margin-top: 10px; font-size: 12px; color: #909399;">
          提交时间：{{ viewRecordData.createdAt | dateFormat }}
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览 -->
    <el-image-viewer v-if="showViewer" :url-list="previewList" @close="showViewer = false" />
  </div>
</template>

<script>
import ElImageViewer from 'element-ui/packages/image/src/image-viewer'
import { hasAction } from '@/utility/permissions'
import ImageUpload from '@/components/ImageUpload.vue'

export default {
  name: 'DudaoYunying',
  components: { ElImageViewer, ImageUpload },
  data () {
    return {
      // 视图模式
      viewMode: 'table',

      // 筛选条件
      selectedMonth: '',
      filterSupervisorId: '',
      keyword: '',

      // 数据
      projects: [],
      loading: false,

      // 身份相关
      // 范围显隐标记（board 接口下发）：仅自己档隐藏督导手选筛选
      myRights: { isSelfOnly: false },
      level: 0,
      department: '',
      job: '',
      employeeId: '',

      // 督导列表（主管/管理员用）
      supervisorList: [],

      base: window.sessionStorage.getItem('base') || '',

      // 提交弹窗
      submitDialogVisible: false,
      submitting: false,
      submitForm: {
        projectId: '',
        projectName: '',
        type: '',
        weekIndex: null,
        inspectionIndex: null,
        images: [],
        remark: ''
      },

      // 查看弹窗
      viewDialogVisible: false,
      viewRecordData: null,

      // 图片预览
      showViewer: false,
      previewList: [],

      // 分页
      pagination: {
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    showSupervisorFilter () {
      // M3 后端已三档化：管理视角=非仅自己
      return !this.myRights.isSelfOnly
    },
    pagedProjects () {
      const start = (this.pagination.page - 1) * this.pagination.pageSize
      return this.projects.slice(start, start + this.pagination.pageSize)
    },
    submitDialogTitle () {
      const project = this.submitForm.projectName
      if (this.submitForm.type === 'weekly') {
        return `${project} - ${this.submitForm.weekIndex}周清`
      }
      return `${project} - 巡检${this.submitForm.inspectionIndex}`
    },
    viewDialogTitle () {
      if (!this.viewRecordData) return ''
      const p = this.viewRecordData.projectName
      if (this.viewRecordData.type === 'weekly') {
        return `${p} - ${this.viewRecordData.weekIndex}周清`
      }
      return `${p} - 巡检${this.viewRecordData.inspectionIndex}`
    }
  },
  created () {
    this.base = window.sessionStorage.getItem('base') || ''

    // 默认当月
    const now = new Date()
    this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

    this.loadBoard()
  },
  methods: {
    // 加载督导列表
    async loadSupervisorList () {
      try {
        const { data: res } = await this.$http.get('process/getEmployeeList', {
          params: { role: 'operatingSupervisor' }
        })
        if (res.meta.status === 200) {
          this.supervisorList = res.data
        }
      } catch (err) {
        console.error('获取督导列表失败', err)
      }
    },

    // 加载工作台数据
    async loadBoard () {
      if (!this.selectedMonth) return
      this.loading = true
      try {
        const [year, month] = this.selectedMonth.split('-').map(Number)
        const params = { year, month }
        if (this.filterSupervisorId) {
          params.supervisorId = this.filterSupervisorId
        }
        if (this.keyword) {
          params.keyword = this.keyword
        }
        const { data: res } = await this.$http.get('dudao/operating-supervisor/board', { params })
        if (res.meta.status === 200) {
          this.projects = res.data.projects
          if (res.data.myRights) this.myRights = res.data.myRights
          // 督导列表只对非仅自己用户拉取（此时 myRights 已就位）
          if (this.showSupervisorFilter && !this.supervisorList.length) this.loadSupervisorList()
          this.pagination.page = 1
        } else {
          this.$message.error(res.meta.msg)
        }
      } catch (err) {
        console.error('加载工作台失败', err)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 计算完成数
    getCompletedCount (project) {
      let count = project.weekly.filter(Boolean).length
      count += project.monthly.filter(Boolean).length
      return count
    },

    // 打开提交弹窗
    openSubmitDialog (project, type, index) {
      // 动作资格（G13）：按登录下发的 grantedActions 判断，替代五态 identity 猜测；
      // 后端闸门同源 need('dudaoYunying','提交记录')，该格仅授营运督导
      if (!hasAction('dudaoyunying', '提交记录')) {
        this.$message.warning('只有营运督导可以提交')
        return
      }
      this.submitForm.projectId = project.projectId
      this.submitForm.projectName = project.projectName
      this.submitForm.type = type
      if (type === 'weekly') {
        this.submitForm.weekIndex = index
        this.submitForm.inspectionIndex = null
      } else {
        this.submitForm.weekIndex = null
        this.submitForm.inspectionIndex = index
      }
      this.submitForm.images = []
      this.submitForm.remark = ''
      this.submitDialogVisible = true
    },

    // 提交记录
    async submitRecord () {
      if (this.submitForm.images.length === 0) {
        this.$message.warning('请至少上传一张图片')
        return
      }

      this.submitting = true
      try {
        const [year, month] = this.selectedMonth.split('-').map(Number)
        const data = {
          projectId: this.submitForm.projectId,
          type: this.submitForm.type,
          year,
          month,
          images: this.submitForm.images,
          remark: this.submitForm.remark
        }
        if (this.submitForm.type === 'weekly') {
          data.weekIndex = this.submitForm.weekIndex
        } else {
          data.inspectionIndex = this.submitForm.inspectionIndex
        }

        const { data: res } = await this.$http.post('dudao/operating-supervisor/record', data)
        if (res.meta.status === 200) {
          this.$message.success('提交成功')
          this.submitDialogVisible = false
          this.loadBoard()
        } else {
          this.$message.error(res.meta.msg)
        }
      } catch (err) {
        console.error('提交失败', err)
        this.$message.error('提交失败')
      } finally {
        this.submitting = false
      }
    },

    // 查看记录
    async viewRecord (project, type, index) {
      try {
        const [year, month] = this.selectedMonth.split('-').map(Number)
        const params = { year, month, type }
        if (type === 'weekly') {
          params.weekIndex = index
        } else {
          params.inspectionIndex = index
        }
        const { data: res } = await this.$http.get(`dudao/operating-supervisor/record/${project.projectId}`, { params })
        if (res.meta.status === 200) {
          this.viewRecordData = res.data
          this.viewDialogVisible = true
        } else {
          this.$message.error(res.meta.msg)
        }
      } catch (err) {
        console.error('查看记录失败', err)
      }
    },

    // 图片预览
    handleUploadPreview (imageUrl) {
      if (imageUrl) {
        this.previewImage(imageUrl)
      }
    },

    // 预览图片
    previewImage (url) {
      this.previewList = [url]
      this.showViewer = true
    },

    // 重置提交表单
    resetSubmitForm () {
      this.submitForm = {
        projectId: '',
        projectName: '',
        type: '',
        weekIndex: null,
        inspectionIndex: null,
        images: [],
        remark: ''
      }
    },

    // 分页
    handleSizeChange (val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
    },
    handlePageChange (val) {
      this.pagination.page = val
    },

    // 点击督导名称筛选
    filterBySupervisor (row) {
      if (!this.showSupervisorFilter) return
      if (this.filterSupervisorId === row.supervisorId) {
        this.filterSupervisorId = ''
      } else {
        this.filterSupervisorId = row.supervisorId
      }
      this.loadBoard()
    }
  }
}
</script>

<style scoped>
.search-bar {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 15px;
}

.search-form {
  flex: 1;
}

.stat-card {
  flex-shrink: 0;
  text-align: center;
  padding: 8px 36px;
  border-radius: 4px;
  background: linear-gradient(135deg, #409EFF, #66b1ff);
  color: #fff;
}

.stat-card-label {
  font-size: 12px;
  opacity: 0.85;
}

.stat-card-value {
  font-size: 26px;
  font-weight: bold;
  line-height: 1.3;
}

.status-done {
  color: #67C23A;
  font-size: 12px;
  cursor: pointer;
}

.status-undone {
  color: #F56C6C;
  font-size: 12px;
  cursor: pointer;
}

.status-done:hover,
.status-undone:hover {
  text-decoration: underline;
}

.supervisor-link {
  color: #409EFF;
  cursor: pointer;
}

.supervisor-link:hover {
  text-decoration: underline;
}

.supervisor-tag {
  cursor: pointer !important;
}

.supervisor-tag:hover {
  color: #409EFF;
  border-color: #b3d8ff;
  background-color: #ecf5ff;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

.project-card {
  cursor: default;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-opening-info {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.no-opening {
  color: #C0C4CC;
}

.card-progress {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  color: #606266;
}

.card-items {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.card-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  padding: 4px 0;
}

.card-item:hover {
  color: #409EFF;
}

.view-images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.view-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
}

.view-image:hover {
  opacity: 0.8;
}
</style>
